import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function GadgetSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const sectionRef = useRef<HTMLElement>(null);

  // Countdown to March 2026 Makuake launch
  useEffect(() => {
    const targetDate = new Date('2026-03-01T00:00:00+09:00');
    const calc = () => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        });
      } else {
        // If the countdown is over, stop the timer
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    calc();
    const timer = setInterval(calc, 1000);
    return () => clearInterval(timer);
  }, []);

  // Intersection observer for animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <section
      ref={sectionRef}
      className="relative py-20 sm:py-28 md:py-36 bg-[#0A0A0A] overflow-hidden"
      aria-labelledby="gadget-heading"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#8E7AB5]/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#8E7AB5]/3 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/[0.02] rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/[0.03] rounded-full" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Top badge */}
        <div
          className={`text-center mb-10 sm:mb-14 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#06C755] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#06C755]"></span>
            </span>
            <span className="text-xs sm:text-sm text-white/70 font-light tracking-wider">
              2026年3月 Makuake公開予定
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 sm:gap-16 lg:gap-20 items-center">
          {/* Left: Product image */}
          <div
            className={`relative transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-neutral-100">
              <img
                src="https://public.readdy.ai/ai/img_res/a4b6ed8797acd755266cb80cdf654faf.jpg"
                alt="MeVER Atelier Folio - クリエイティブワークを加速"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                width="800"
                height="1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
            </div>

            {/* Floating badge */}
            <div
              className={`absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-2xl px-6 py-4 transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-neutral-900">
                  <i className="ri-palette-line text-lg text-white" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-xs font-medium text-neutral-400 tracking-wider uppercase">
                    For Creators
                  </p>
                  <p className="text-base font-light text-neutral-900 tracking-wide">
                    iPad Pro Ready
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Content + CTA */}
          <div
            className={`transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <h2
              id="gadget-heading"
              className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 tracking-tight leading-tight"
            >
              Atelier Folio
              <br />
              <span className="text-[#8E7AB5]">for iPad</span>
            </h2>

            <p className="text-base sm:text-lg text-white/50 font-light mb-6 sm:mb-8 leading-relaxed">
              iPadを、あなたのアトリエに。<br />
              クリエイティブワークに特化した、革新的なiPadスタンド＆フォリオケース。
            </p>

            {/* Countdown timer */}
            <div className="mb-8 sm:mb-10">
              <p className="text-xs text-white/40 font-light tracking-wider uppercase mb-3">
                Makuake公開まで
              </p>
              <div className="flex items-center gap-2 sm:gap-3">
                {[
                  { value: timeLeft.days, label: '日' },
                  { value: timeLeft.hours, label: '時' },
                  { value: timeLeft.minutes, label: '分' },
                  { value: timeLeft.seconds, label: '秒' },
                ].map((item, i) => (
                  <div key={item.label} className="flex items-center gap-2 sm:gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-white/5 border border-white/10 rounded-lg">
                        <span
                          className={`text-xl sm:text-2xl font-bold tracking-tight ${
                            item.label === '秒' ? 'text-[#8E7AB5]' : 'text-white'
                          }`}
                        >
                          {pad(item.value)}
                        </span>
                      </div>
                      <span className="text-[10px] text-white/30 mt-1.5">{item.label}</span>
                    </div>
                    {i < 3 && <span className="text-white/20 text-lg font-light mb-4">:</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* LINE CTA - Primary */}
            <div className="bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 rounded-xl p-5 sm:p-6 mb-4">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#06C755] rounded-xl flex-shrink-0">
                  <i className="ri-line-fill text-white text-xl" aria-hidden="true"></i>
                </div>
                <div>
                  <h3 className="text-white font-medium text-sm sm:text-base mb-1">
                    公式LINEで先行情報をGET
                  </h3>
                  <p className="text-white/40 text-xs font-light leading-relaxed">
                    Makuake公開前に最速でお届け。先行割引・限定特典の情報もLINEだけで配信します。
                  </p>
                </div>
              </div>

              {/* Benefits list */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5">
                <div className="flex items-center gap-2 px-3 py-2 bg-[#06C755]/10 rounded-lg">
                  <i className="ri-gift-2-line text-[#06C755] text-sm" aria-hidden="true"></i>
                  <span className="text-xs text-white/70 font-light">先行割引のご案内</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-[#06C755]/10 rounded-lg">
                  <i className="ri-notification-3-line text-[#06C755] text-sm" aria-hidden="true"></i>
                  <span className="text-xs text-white/70 font-light">公開日の最速通知</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-[#06C755]/10 rounded-lg">
                  <i className="ri-vip-crown-line text-[#06C755] text-sm" aria-hidden="true"></i>
                  <span className="text-xs text-white/70 font-light">LINE限定特典</span>
                </div>
              </div>

              <a
                href="https://lin.ee/d6PoCFQ6"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center justify-center gap-2 w-full px-6 py-3.5 sm:py-4 bg-[#06C755] hover:bg-[#05B34A] text-white text-sm sm:text-base font-medium tracking-wide transition-all duration-300 rounded-xl cursor-pointer whitespace-nowrap hover:shadow-lg hover:shadow-[#06C755]/20 group"
              >
                <i className="ri-line-fill text-xl" aria-hidden="true"></i>
                <span>公式LINEで事前登録する</span>
                <i
                  className="ri-arrow-right-s-line text-lg group-hover:translate-x-1 transition-transform"
                  aria-hidden="true"
                ></i>
              </a>

              <p className="text-center text-[10px] text-white/25 mt-3 font-light">
                無料・いつでもブロック可能
              </p>
            </div>

            {/* Secondary: Detail page link */}
            <Link
              to="/atelier-folio"
              className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white text-sm font-light tracking-wide transition-all duration-300 rounded-xl cursor-pointer whitespace-nowrap"
            >
              製品の詳細を見る
              <i className="ri-arrow-right-line text-sm" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
